import {
  ferryApi,
  type AmendFerryBookingRequest,
  type CreateFerryBookingRequest,
  type FerryBooking,
} from '@my/ferry-api';
import {
  useAmendFerryBooking,
  useCancelFerryBooking,
  useCreateFerryBooking,
} from '@my/ferry-api/hooks';
import type { OrderBooking, UpdateOrderBookingRequest } from '@my/tms-api';
import { useDeleteOrderBooking, useUpdateOrderBooking } from '@my/tms-api/hooks';
import type { BookingFormValues } from '../orders/lib/types';
import { utcToZoned, zonedToUtc } from './portTime';
import { ferryBookingAccepted } from './status';

// The browser orchestrates the two services (decision of 16.10.26): the TMS
// booking is created first so its id can be the Ferry booking's
// `externalReference`, then Ferry books at the carrier, then the answer is
// written back onto the TMS row. Every step is recoverable from that link.

/** Stena prints this on its paperwork and allows 17 characters; a longer order number falls back to the booking id. */
export function ferryCustomerReference(
  orderNumber: string | null | undefined,
  tmsBookingId: string,
): string {
  const n = orderNumber?.trim() ?? '';
  return n && n.length <= 17 ? n : `TMS-${tmsBookingId.slice(0, 8)}`;
}

/** A full-replace PUT body echoing the booking as stored, for a change to one or two fields. */
export function updateBodyFrom(b: OrderBooking): UpdateOrderBookingRequest {
  return {
    bookingId: b.id,
    carrierPartyId: b.carrierPartyId ?? null,
    agentPartyId: b.agentPartyId ?? null,
    carrierRouteId: b.carrierRouteId ?? null,
    routeCode: b.routeCode ?? null,
    routeName: b.routeName ?? null,
    carrierReference: b.carrierReference ?? null,
    vessel: b.vessel ?? null,
    voyageNumber: b.voyageNumber ?? null,
    sailDate: b.sailDate ?? null,
    sailTime: b.sailTime ?? null,
    typicalDurationMinutes: b.typicalDurationMinutes ?? null,
    typicalCheckInMinutes: b.typicalCheckInMinutes ?? null,
    typicalDriveOffMinutes: b.typicalDriveOffMinutes ?? null,
    bookingType: b.bookingType,
    isAccompanied: b.isAccompanied,
    numberOfDrivers: b.numberOfDrivers ?? null,
    vehicleRegistration: b.vehicleRegistration ?? null,
    trailerNumber: b.trailerNumber ?? null,
    customsReference: b.customsReference ?? null,
    clearanceStatus: b.clearanceStatus ?? null,
    notes: b.notes ?? null,
    ferryBookingId: b.ferryBookingId ?? null,
  };
}

/** What stops a Ferry-linked booking being sent; checked before the TMS row is created, so nothing has to be undone. */
export function ferryBlockingMessage(v: BookingFormValues): string | null {
  if (!v.ferryRouteCode || !v.ferryCarrierId) return null;
  // The carrier file maps nothing for this booking type, or Ferry's type list is still loading.
  if (!v.ferryVehicleTypeCode)
    return 'No vehicle type is mapped at the carrier for this booking type. Switch the integration off to save a manual booking.';
  const sailing = sailingMessage(v);
  if (sailing) return sailing;
  if (v.ferryCredentialRequired && !v.ferryCredentialId)
    return 'Pick which carrier account to book under.';
  return null;
}

/** What stops a moved sailing being sent; checked before anything is saved. */
export function ferryAmendBlockingMessage(v: BookingFormValues): string | null {
  if (!v.sailDate) return 'Pick the sail date.';
  return sailingMessage(v);
}

/** The typed time against the carrier's timetable for the day, when Ferry holds one. */
function sailingMessage(v: BookingFormValues): string | null {
  if (!v.sailTime) return 'Type the departure time.';
  if (v.ferryDayDepartures && !v.ferrySailingUtc)
    return `No sailing at ${v.sailTime.slice(0, 5)} that day. Departures: ${v.ferryDayDepartures}.`;
  return null;
}

export type SendToFerryOutcome =
  | { outcome: 'confirmed'; booking: FerryBooking }
  | { outcome: 'refused'; booking: FerryBooking; message: string }
  // The TMS booking was undone; the form may try again.
  | { outcome: 'failed'; message: string }
  // The TMS booking stays on the order; the carrier's answer is read from it later.
  | { outcome: 'unknown'; message: string };

export function useFerryBookingFlow() {
  const createFerry = useCreateFerryBooking();
  const amendFerry = useAmendFerryBooking();
  const cancelFerry = useCancelFerryBooking();
  const updateTms = useUpdateOrderBooking();
  const deleteTms = useDeleteOrderBooking();
  const isBusy =
    createFerry.isPending ||
    amendFerry.isPending ||
    cancelFerry.isPending ||
    updateTms.isPending ||
    deleteTms.isPending;

  /**
   * Book a just-created TMS booking at the carrier and write the answer back.
   * An HTTP failure deletes the TMS booking again (the order is as it was)
   * unless Ferry turns out to hold the booking; a carrier refusal keeps it,
   * with the reason, so the operator can see why.
   */
  async function sendToFerry(args: {
    orderId: string;
    tmsBooking: OrderBooking;
    values: BookingFormValues;
    orderNumber: string | null | undefined;
  }): Promise<SendToFerryOutcome> {
    const { orderId, tmsBooking, values, orderNumber } = args;
    const zone = values.ferryOriginZone || 'UTC';
    const scheduledDeparture =
      values.ferrySailingUtc ||
      (values.sailDate && values.sailTime
        ? zonedToUtc(values.sailDate, values.sailTime, zone)
        : null);

    const body: CreateFerryBookingRequest = {
      organisationId: tmsBooking.organisationId,
      carrierId: values.ferryCarrierId,
      credentialId: values.ferryCredentialId || null,
      externalReference: tmsBooking.id,
      routeCode: values.ferryRouteCode,
      scheduledDeparture,
      customerReference: ferryCustomerReference(orderNumber, tmsBooking.id),
      vehicleTypeCode: values.ferryVehicleTypeCode,
      vehicleRegistration: values.isAccompanied ? values.vehicleRegistration.trim() || null : null,
      trailerRegistration: values.trailerNumber.trim() || null,
      lengthMetres: values.ferryLengthMetres ? Number(values.ferryLengthMetres) || null : null,
      heightMetres: null,
      widthMetres: null,
      cargoWeightKg: Number.parseInt(values.ferryCargoWeightKg, 10) || 0,
      numberOfDrivers: values.isAccompanied ? Number.parseInt(values.numberOfDrivers, 10) || 0 : 0,
      isDangerousGoods: values.ferryDangerousGoods,
      livestock: false,
      // A reefer books one plug-in and its temperature, as the old desktop did.
      numberOfPluginCables: values.ferryPlugIn ? 1 : null,
      pluginTemperature: values.ferryPlugIn ? values.ferryPluginTemperature.trim() || null : null,
      ukCustomsProcedure: values.ferryUkCustomsProcedure || 'Unknown',
      exportId: values.ferryExportId.trim() || null,
      importId: values.ferryImportId.trim() || null,
      isTransit: null,
      driverFirstName: null,
      driverSurname: null,
      driverDateOfBirth: null,
      keepCapacity: false,
    };

    let ferryBooking: FerryBooking;
    try {
      // One key per attempt; a retry of THIS attempt would resend it.
      ferryBooking = await createFerry.mutateAsync({ body, idempotencyKey: crypto.randomUUID() });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'The ferry service could not be reached.';
      // Ferry saves its row before calling the carrier, so after a lost answer
      // it usually holds the booking under our id; only one it never got is undone.
      let found: FerryBooking | null;
      try {
        const matches = await ferryApi.getBookings(tmsBooking.organisationId, {
          externalReference: tmsBooking.id,
        });
        found = matches[0] ?? null;
      } catch {
        return {
          outcome: 'unknown',
          message: `${reason} The booking was kept on the order; open it to see whether the carrier holds it.`,
        };
      }
      if (!found) {
        try {
          await deleteTms.mutateAsync({ orderId, bookingId: tmsBooking.id });
        } catch {
          return {
            outcome: 'unknown',
            message: `${reason} The booking stayed on the order without a ferry booking; delete it and try again.`,
          };
        }
        return { outcome: 'failed', message: reason };
      }
      ferryBooking = found;
    }

    // Write-back. The link always; the carrier's number, ship and sailing in
    // port time once it accepted. If this PUT fails the panel re-links the
    // booking by externalReference when it is next opened.
    const accepted = ferryBookingAccepted(ferryBooking);
    const sailing = ferryBooking.scheduledDeparture
      ? utcToZoned(ferryBooking.scheduledDeparture, zone)
      : null;
    const patch: Partial<UpdateOrderBookingRequest> = {
      ferryBookingId: ferryBooking.id,
      ...(accepted
        ? {
            carrierReference: String(ferryBooking.carrierBookingNumber),
            vessel: ferryBooking.shipName ?? tmsBooking.vessel ?? null,
            ...(sailing ? { sailDate: sailing.date, sailTime: sailing.time } : {}),
          }
        : {}),
    };
    try {
      await updateTms.mutateAsync({ orderId, body: { ...updateBodyFrom(tmsBooking), ...patch } });
    } catch {
      // Recovered on next open; nothing to do here.
    }

    const status = ferryBooking.integrationStatus;
    if (status === 'Pending' || status === 'Requesting') {
      return {
        outcome: 'unknown',
        message:
          "The ferry service is still booking it; open the booking to see the carrier's answer.",
      };
    }
    if (status === 'Failed') {
      return {
        outcome: 'refused',
        booking: ferryBooking,
        message: ferryBooking.message ?? 'The carrier refused the booking.',
      };
    }
    return { outcome: 'confirmed', booking: ferryBooking };
  }

  /**
   * Re-send a linked booking with its changed sailing and unit; what only
   * Ferry holds goes back as it is. Keep capacity is on, as the legacy
   * service always sent it, so a move keeps the block it leaves. Check
   * `integrationStatus` on the answer: a refusal is not an error.
   */
  async function amendAtFerry(args: {
    ferryBooking: FerryBooking;
    routeCode: string;
    zone: string;
    sailingChanged: boolean;
    values: BookingFormValues;
  }): Promise<FerryBooking> {
    const { ferryBooking: b, routeCode, zone, sailingChanged, values } = args;
    // An unchanged sailing goes back exactly as the carrier holds it.
    const scheduledDeparture = sailingChanged
      ? values.ferrySailingUtc || zonedToUtc(values.sailDate, values.sailTime.slice(0, 5), zone)
      : b.scheduledDeparture;
    const body: AmendFerryBookingRequest = {
      organisationId: b.organisationId,
      routeCode,
      scheduledDeparture,
      customerReference: b.customerReference,
      vehicleTypeCode: b.vehicleTypeCode,
      vehicleRegistration: values.isAccompanied ? values.vehicleRegistration.trim() || null : null,
      trailerRegistration: values.trailerNumber.trim() || null,
      lengthMetres: b.lengthMetres,
      heightMetres: b.heightMetres,
      widthMetres: b.widthMetres,
      cargoWeightKg: b.cargoWeightKg,
      numberOfDrivers: values.isAccompanied ? Number.parseInt(values.numberOfDrivers, 10) || 0 : 0,
      isDangerousGoods: b.isDangerousGoods,
      livestock: b.livestock,
      numberOfPluginCables: b.numberOfPluginCables ?? null,
      pluginTemperature: b.pluginTemperature ?? null,
      ukCustomsProcedure: b.ukCustomsProcedure,
      exportId: b.exportId ?? null,
      importId: b.importId ?? null,
      isTransit: b.isTransit ?? null,
      driverFirstName: b.driverFirstName ?? null,
      driverSurname: b.driverSurname ?? null,
      driverDateOfBirth: b.driverDateOfBirth ?? null,
      keepCapacity: true,
    };
    return amendFerry.mutateAsync({ id: b.id, body });
  }

  /**
   * Cancel at the carrier before the TMS booking is deleted. Returns the
   * carrier's reason when it refused, so the TMS booking is kept; a booking it
   * never accepted has nothing to cancel.
   */
  async function cancelAtFerry(args: {
    ferryBooking: FerryBooking;
    keepCapacity: boolean;
  }): Promise<string | null> {
    if (!ferryBookingAccepted(args.ferryBooking)) return null;
    const answer = await cancelFerry.mutateAsync({
      id: args.ferryBooking.id,
      keepCapacity: args.keepCapacity,
    });
    return answer.carrierStatus === 'Cancelled'
      ? null
      : (answer.message ?? 'The carrier did not cancel the booking.');
  }

  return { sendToFerry, amendAtFerry, cancelAtFerry, isBusy };
}
